/*
 * wallace-header-overlay.js
 * ------------------------------------------------------------
 * Drives the solid-state triggers for Wallace Header v2's transparent overlay,
 * and keeps body.header-overlay in sync with the DOM so it also works inside the
 * theme editor (where section inline scripts are not re-run on preview
 * re-render). All appearance lives in wallace-header-overlay.css.
 *
 *   body.header-overlay  present when the first #MainContent section opts in via
 *                        [data-overlay-header="true"] (the home hero). On the
 *                        storefront the hero's inline script also sets this early
 *                        to avoid a flash; here we re-affirm it and, crucially,
 *                        re-detect it on shopify:section:load / :reorder.
 *   body.header-solid    added as soon as the page is scrolled (scrollY > 8),
 *                        removed at the very top (scrollY <= 8). Not relative to
 *                        the hero height, so the solid state never depends on the
 *                        hero's geometry.
 *   body.header-hover    pointer over the header ("equivalent root" for hover).
 *   body.menu-open       mirrors side-menu.active (fallback for no :has support).
 * ------------------------------------------------------------
 */
(function () {
  var body = document.body;
  var SOLID_AT = 8; // px from the top before the header goes solid

  function firstSection() {
    return document.querySelector('#MainContent .shopify-section');
  }

  function headerEl() {
    return document.querySelector('.section-header');
  }

  /* Visual box: sticky-header.header-wrapper owns the two rows + background.
     Fall back to the section wrapper if the custom element is missing. */
  function headerBox() {
    var section = headerEl();
    if (!section) return null;
    return section.querySelector('.header-wrapper') || section.querySelector('header') || section;
  }

  /* Overlay is on when the first main section opts in. */
  function syncOverlayClass() {
    var first = firstSection();
    var wantsOverlay = !!(first && first.querySelector('[data-overlay-header="true"]'));
    body.classList.toggle('header-overlay', wantsOverlay);
  }

  /* Announcement offset, measured ONCE from the rendered .announcement-bar
     (not #announcement-bar, not the header's own rect — that already includes
     sticky/inline top and is circular). 0 when the section is disabled, absent,
     or the shopper has dismissed it. */
  var announcementTop = null;

  function announcementOffset() {
    if (announcementTop !== null) return announcementTop;
    try {
      if (window.sessionStorage && sessionStorage.getItem('announcementStatus') === 'closed') {
        announcementTop = 0;
        return 0;
      }
    } catch (e) {
      /* sessionStorage can throw in a sandboxed preview */
    }
    var bar = document.querySelector('.announcement-bar');
    announcementTop = bar && bar.offsetHeight ? bar.offsetHeight : 0;
    return announcementTop;
  }

  /* The v2 header writes style.top on .section-header from the announcement
     height. Under overlay that inline value must not stack with our pin —
     zero it on the section and the wrapper in both states. */
  function clearInlineTops() {
    var section = headerEl();
    var box = headerBox();
    if (section) section.style.removeProperty('top');
    if (box && box !== section) box.style.removeProperty('top');
  }

  /* --header-height is the header's *natural* (non-fixed) height, written
     on every ResizeObserver callback (rAF-debounced). Measurement flips
     .is-measuring so the wrapper is position:static; height:auto — the
     same containing block as a collection-page header — then reads
     scrollHeight/offsetHeight. Using the fixed box's offsetHeight would
     be circular. Side-menu top is announcement offset + that value. */
  var heightObserver = null;
  var heightTick = false;
  var measuring = false;

  function clearSideMenuTop() {
    var drawer = document.querySelector('.side-menu__drawer');
    var backdrop = document.querySelector('.side-menu__backdrop');
    if (drawer) drawer.style.removeProperty('top');
    if (backdrop) backdrop.style.removeProperty('top');
  }

  function applySideMenuTop() {
    var root = document.documentElement;
    var top = announcementOffset();
    var height = parseInt(root.style.getPropertyValue('--header-height'), 10) || 0;
    var bottom = top + height;
    root.style.setProperty('--wallace-overlay-header-bottom', bottom + 'px');

    var desktop = window.matchMedia('(min-width: 990px)').matches;
    var drawer = document.querySelector('.side-menu__drawer');
    var backdrop = document.querySelector('.side-menu__backdrop');
    if (desktop && height > 0) {
      if (drawer) drawer.style.top = bottom + 'px';
      if (backdrop) backdrop.style.top = bottom + 'px';
    } else {
      clearSideMenuTop();
    }
  }

  function measureNaturalHeaderHeight() {
    var box = headerBox();
    if (!box) return 0;
    measuring = true;
    box.classList.add('is-measuring');
    var header = box.querySelector('header') || box;
    var height = Math.max(
      header.scrollHeight || 0,
      header.offsetHeight || 0,
      box.scrollHeight || 0,
      box.offsetHeight || 0
    );
    box.classList.remove('is-measuring');
    measuring = false;
    return Math.max(0, Math.round(height));
  }

  function writeHeaderHeight() {
    heightTick = false;
    if (!body.classList.contains('header-overlay')) return;
    if (measuring) return;
    var height = measureNaturalHeaderHeight();
    if (height > 0) {
      document.documentElement.style.setProperty('--header-height', height + 'px');
    }
    applySideMenuTop();
  }

  function onHeaderResize() {
    if (measuring || heightTick) return;
    heightTick = true;
    window.requestAnimationFrame(writeHeaderHeight);
  }

  function unbindHeightObserver() {
    if (heightObserver) {
      heightObserver.disconnect();
      heightObserver = null;
    }
    var box = headerBox();
    if (box) box._wallaceHeightObserved = false;
  }

  function bindHeightObserver() {
    var box = headerBox();
    if (!box) return;
    if (box._wallaceHeightObserved && heightObserver) {
      writeHeaderHeight();
      return;
    }
    unbindHeightObserver();
    if ('ResizeObserver' in window) {
      heightObserver = new ResizeObserver(onHeaderResize);
      heightObserver.observe(box);
      box._wallaceHeightObserved = true;
    }
    writeHeaderHeight();
  }

  function syncHeaderMetrics() {
    var root = document.documentElement;
    if (!body.classList.contains('header-overlay')) {
      root.style.removeProperty('--wallace-overlay-header-top');
      root.style.removeProperty('--wallace-overlay-header-bottom');
      clearSideMenuTop();
      announcementTop = null;
      unbindHeightObserver();
      return;
    }

    clearInlineTops();
    root.style.setProperty('--wallace-overlay-header-top', announcementOffset() + 'px');
    bindHeightObserver();
  }

  /* Solid as soon as the user scrolls off the very top. rAF-throttled. */
  var ticking = false;

  function updateSolid() {
    ticking = false;
    if (!body.classList.contains('header-overlay')) {
      body.classList.remove('header-solid');
      return;
    }
    var scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    body.classList.toggle('header-solid', scrollY > SOLID_AT);
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(updateSolid);
  }

  /* Hover: solid while the pointer is over the header. Bound once. */
  function bindHover() {
    var header = headerEl();
    var wrapper = header ? header.querySelector('.header-wrapper') || header : null;
    if (!wrapper || wrapper._wallaceOverlayHoverBound) return;
    wrapper._wallaceOverlayHoverBound = true;
    wrapper.addEventListener('mouseenter', function () {
      body.classList.add('header-hover');
    });
    wrapper.addEventListener('mouseleave', function () {
      body.classList.remove('header-hover');
    });
  }

  /* Side menu open: solid (fallback for browsers without :has). Bound once. */
  function bindSideMenu() {
    var sideMenu = document.querySelector('side-menu');
    if (!sideMenu || sideMenu._wallaceOverlayMenuBound || !('MutationObserver' in window)) return;
    sideMenu._wallaceOverlayMenuBound = true;
    var sync = function () {
      body.classList.toggle('menu-open', sideMenu.classList.contains('active'));
      syncHeaderMetrics();
    };
    new MutationObserver(sync).observe(sideMenu, {
      attributes: true,
      attributeFilter: ['class'],
    });
    sync();
  }

  function init() {
    syncOverlayClass();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('load', function () {
      updateSolid();
      syncHeaderMetrics();
    });
    window.addEventListener('resize', function () {
      announcementTop = null;
      syncHeaderMetrics();
    });
    bindHover();
    bindSideMenu();
    updateSolid();
    // After a frame: announcement script has written (and we have cleared)
    // inline top; pin + content-height are applied.
    window.requestAnimationFrame(syncHeaderMetrics);
  }

  /* Theme editor: sections re-render without re-running their inline scripts, so
     re-detect the overlay flag whenever a section loads or the order changes, and
     re-affirm on DOMContentLoaded in design mode. */
  document.addEventListener('shopify:section:load', function () {
    syncOverlayClass();
    bindHover();
    bindSideMenu();
    updateSolid();
    window.requestAnimationFrame(syncHeaderMetrics);
  });
  document.addEventListener('shopify:section:reorder', function () {
    syncOverlayClass();
    updateSolid();
    window.requestAnimationFrame(syncHeaderMetrics);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (window.Shopify && window.Shopify.designMode) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', syncOverlayClass);
    } else {
      syncOverlayClass();
    }
  }
})();
